import React, { ReactNode } from 'react';
import { mq } from '@kvdbil/components';
import styled, { DefaultTheme, StyledComponent } from 'styled-components';
import { SiteMessageType } from '../interfaces/Message';
import { Close } from '~/App/shared/components/IconButtons/Close';

const Wrapper = styled.div`
  padding: 0.5rem 0;
  margin-top: -1px;
  border-width: 1px 0px;

  ${mq('tablet')} {
    padding: 1rem 0;
  }
`;

const CenterContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  max-width: 1024px;
  margin: 0 auto;
  width: calc(100% - (0.75rem * 2));
  gap: 0.5rem;

  ${mq('tablet')} {
    width: calc(100% - (1rem * 2));
  }
`;

const CloseButtonWrapper = styled.span`
  ${mq('tablet')} {
    order: 3;
  }
  ${mq(null, 'tablet')} {
    order: 1;
  }
`;

const Content = styled.div`
  order: 2;
  flex: 1;
  color: currentColor;
  display: inline-block;
  vertical-align: middle;
  width: calc(100% - 2.5rem);

  a {
    text-decoration: underline;
    color: currentColor;
  }
`;

const InformationWrapper = styled(Wrapper)`
  background-color: ${({ theme }) => theme.colors.info.lighter};
  border: solid 1px ${({ theme }) => theme.colors.info.light};
`;

const USPWrapper = styled(Wrapper)`
  background-color: ${({ theme }) => theme.colors.background.gray};
  color: ${({ theme }) => theme.colors.info.dark};
  text-align: center;
`;

const ErrorWrapper = styled(Wrapper)`
  background-color: ${({ theme }) => theme.colors.error.lighter};
`;

const SuccessWrapper = styled(Wrapper)`
  background-color: ${({ theme }) => theme.colors.success.lighter};
`;

type StyledDiv = StyledComponent<
  'div',
  DefaultTheme,
  Record<string, unknown>,
  never
>;

type ComponentMapping = { [key in SiteMessageType]: StyledDiv };

const containers: Partial<ComponentMapping> = {
  USP: USPWrapper,
  Error: ErrorWrapper,
  Success: SuccessWrapper
};

export interface SiteMessageProps {
  children: ReactNode;
  type?: SiteMessageType;
  onDismiss(): void;
}

export const SiteMessage = ({
  children,
  type = 'Information',
  onDismiss
}: SiteMessageProps) => {
  const Container: StyledDiv = containers?.[type] ?? InformationWrapper;

  return (
    <Container
      role={type === 'Error' ? 'alert' : undefined}
      aria-live={type === 'Error' ? 'assertive' : 'polite'}
    >
      <CenterContainer>
        <Content>{children}</Content>
        {type !== 'USP' && (
          <CloseButtonWrapper>
            <Close onClick={onDismiss} iconSize={1.5} />
          </CloseButtonWrapper>
        )}
      </CenterContainer>
    </Container>
  );
};

export default SiteMessage;
